import React from "react";

import Box from "@mui/material/Box";

import negativeFeedback from "../../assets/images/negative-feedback-illustration.svg";
import recoveryContact from "../../assets/images/recovery-contact-illustration.svg";
import successCheck from "../../assets/images/success-icon.svg";
import giftBasket from "../../assets/images/gift-basket-v2.svg";
import giftHeart from "../../assets/images/gift-heart-v2.svg";
import type {
  FlowComponentRenderProps,
  VisualAssetComponentConfig,
} from "../types";

function GiftBoxThankYou({
  id,
  maxWidth = 315,
}: {
  id: string;
  maxWidth?: number;
}) {
  return (
    <Box
      aria-hidden="true"
      data-testid={`visual-asset-${id}`}
      sx={{
        aspectRatio: "315 / 166",
        containerType: "inline-size",
        maxWidth,
        mx: "auto",
        overflow: "hidden",
        position: "relative",
        width: "100%",
        "@keyframes cxGiftHeartRise": {
          from: { transform: "translateY(35.9494%)" },
          to: { transform: "translateY(0)" },
        },
      }}
    >
      <Box
        data-testid="gift-box-heart-layer"
        sx={{
          animation:
            "cxGiftHeartRise 650ms cubic-bezier(0.22, 1, 0.36, 1) 180ms both",
          inset: 0,
          position: "absolute",
          zIndex: 1,
          "@media (prefers-reduced-motion: reduce)": {
            animation: "none",
            transform: "translateY(0)",
          },
        }}
      >
        <Box
          alt=""
          component="img"
          src={giftHeart}
          sx={{
            height: "52.421%",
            left: "27.0737%",
            position: "absolute",
            top: "1.2048%",
            transform: "scaleY(-1)",
            width: "45.9575%",
          }}
        />
        <Box
          sx={{
            alignItems: "center",
            display: "flex",
            height: "35.8416%",
            justifyContent: "center",
            left: "34.6032%",
            position: "absolute",
            top: "7.2289%",
            width: "28.3768%",
          }}
        >
          <Box
            component="span"
            sx={{
              color: "#FCE2D8",
              fontFamily: "Gilroy, sans-serif",
              fontSize: "8.2076cqw",
              fontWeight: 800,
              letterSpacing: "0.3283cqw",
              lineHeight: 1,
              transform: "rotate(-13.1deg)",
              whiteSpace: "nowrap",
            }}
          >
            Thank
          </Box>
        </Box>
        <Box
          sx={{
            alignItems: "center",
            display: "flex",
            height: "30.7886%",
            justifyContent: "center",
            left: "42.2019%",
            position: "absolute",
            top: "23.8494%",
            width: "16.9366%",
          }}
        >
          <Box
            component="span"
            sx={{
              color: "#FCE2D8",
              fontFamily: "Gilroy, sans-serif",
              fontSize: "8.2076cqw",
              fontWeight: 800,
              letterSpacing: "0.3283cqw",
              lineHeight: 1,
              transform: "rotate(-13.1deg)",
              whiteSpace: "nowrap",
            }}
          >
            you
          </Box>
        </Box>
      </Box>

      <Box
        alt=""
        component="img"
        data-testid="gift-box-basket-layer"
        src={giftBasket}
        sx={{
          height: "101.9199%",
          left: "15.5556%",
          position: "absolute",
          top: "28.3133%",
          width: "66.1604%",
          zIndex: 2,
        }}
      />
      <Box
        sx={{
          background:
            "linear-gradient(180deg, rgba(255, 255, 255, 0) 0%, #FFFFFF 100%)",
          bottom: 0,
          height: "56.0241%",
          left: "8.8889%",
          position: "absolute",
          width: "76.8254%",
          zIndex: 3,
        }}
      />
    </Box>
  );
}

const VisualAssetBlock: React.FC<
  FlowComponentRenderProps<VisualAssetComponentConfig>
> = ({ config }) => {
  if (config.animation === "gift_box_thank_you") {
    return <GiftBoxThankYou id={config.id} maxWidth={config.maxWidth} />;
  }

  const presetUrl =
    config.preset === "negative_feedback"
      ? negativeFeedback
      : config.preset === "recovery_contact"
        ? recoveryContact
      : config.preset === "success_check"
        ? successCheck
        : undefined;
  const assetUrl = config.url || presetUrl;

  if (!assetUrl) return null;

  // Use maxWidth as the layout width (not only a CSS max-width cap). Preset SVGs
  // often have small intrinsic sizes (48–176px), so max-width alone never grows
  // them to Small/Medium/Large chip targets (180/240/315).
  const layoutWidth =
    typeof config.maxWidth === "number" && Number.isFinite(config.maxWidth)
      ? config.maxWidth
      : undefined;
  const sizedWidth =
    typeof layoutWidth === "number" ? `${layoutWidth}px` : undefined;

  return (
    <Box
      alt={config.alt ?? ""}
      component="img"
      data-testid={`visual-asset-${config.id}`}
      src={assetUrl}
      sx={{
        display: "block",
        height: "auto",
        maxWidth: "100%",
        mx: "auto",
        width: sizedWidth ?? (config.preset ? "auto" : "100%"),
      }}
    />
  );
};

export default VisualAssetBlock;
